<script setup lang="ts">
import RAlert from '~/components/organs/Alert.vue'
import { container as ModalsContainer } from 'jenesius-vue-modal'
import { storeToRefs } from 'pinia'
import { useAuth } from '~/store/auth'
import { useGlobals } from '~/store/global'
import { init } from '~/utils/Amplitude'

const authStore = useAuth()
const { isAuth } = storeToRefs(authStore)

const globals = useGlobals()
const { cookie, localStorage, sessionStorage, history } = storeToRefs(globals)

const isIframe = computed(() => {
  return useRoute().path === '/iframe'
})

const auth = (event: MessageEvent) => {
  const data = (typeof event.data === 'string' ? JSON.parse(event.data) : event.data) as object
  if ('action' in data && data.action === 'auth') {
    if ('token' in data && typeof data.token === 'string') {
      authStore.login(data.token).then(() => {
        init()
      }).catch(() => {
        window.parent.postMessage({ action: 'auth-error' }, '*')
      })
    } else {
      window.parent.postMessage({ action: 'auth-error' }, '*')
    }
  }
}

onMounted(() => {
  if (!isIframe.value) {
    Object.defineProperty(document, 'cookie', {
      get: function () {
        return cookie.value
      },
      set: function (newCookie) {
        // Добавляем или обновляем cookie в строке
        const cookieName = newCookie.split('=')[0].trim()
        const existingCookies = cookie.value.split('; ').filter((c: string) => {
          return !c.startsWith(cookieName + '=')
        })
        existingCookies.push(newCookie)
        cookie.value = existingCookies.join('; ')
      },
      configurable: true,
    })

    Object.defineProperty(window, 'localStorage', {
      value: {
        getItem(key: PropertyKey) {
          return Object.prototype.hasOwnProperty.call(localStorage.value, key) ? localStorage.value[key] : null
        },
        setItem(key: PropertyKey, value: { toString: () => unknown }) {
          localStorage.value[key] = value.toString()
        },
        removeItem(key: PropertyKey) {
          delete localStorage.value[key]
        },
        clear() {
          localStorage.value = {}
        },
        key(index: number) {
          const keys = Object.keys(localStorage.value)
          return keys[index] || null
        },
        get length() {
          return Object.keys(localStorage.value).length
        },
      },
      configurable: true,
      writable: true,
    })

    Object.defineProperty(window, 'sessionStorage', {
      value: {
        getItem(key: PropertyKey) {
          return Object.prototype.hasOwnProperty.call(sessionStorage.value, key) ? sessionStorage.value[key] : null
        },
        setItem(key: PropertyKey, value: { toString: () => unknown }) {
          sessionStorage.value[key] = value.toString()
        },
        removeItem(key: PropertyKey) {
          delete sessionStorage.value[key]
        },
        clear() {
          sessionStorage.value = {}
        },
        key(index: number) {
          const keys = Object.keys(sessionStorage.value)
          return keys[index] || null
        },
        get length() {
          return Object.keys(sessionStorage.value).length
        },
      },
      configurable: true,
      writable: true,
    })

    Object.defineProperty(window, 'history', {
      value: {

        get length(): number {
          return history.value.entries.length
        },

        get state(): unknown {
          if (history.value.currentIndex < 0 || history.value.currentIndex >= history.value.entries.length) {
            return null
          }
          return history.value.entries[history.value.currentIndex].state
        },

        pushState(state: unknown, title: string, url?: string | null): void {
          // Удаляем все "будущие" записи, если были переходы назад
          if (history.value.currentIndex < history.value.entries.length - 1) {
            history.value.entries = history.value.entries.slice(0, history.value.currentIndex + 1)
          }
          const entry = { state, title, url: url || null }
          history.value.entries.push(entry)
          history.value.currentIndex++
        },

        replaceState(state: unknown, title: string, url?: string | null): void {
          if (history.value.currentIndex < 0) {
            // Если истории ещё нет — добавляем новую запись
            const entry = { state, title, url: url || null }
            history.value.entries.push(entry)
            history.value.currentIndex = 0
          } else {
            history.value.entries[history.value.currentIndex] = { state, title, url: url || null }
          }
        },

        back(): void {
          if (history.value.currentIndex > 0) {
            history.value.currentIndex--
          }
        },

        forward(): void {
          if (history.value.currentIndex < history.value.entries.length - 1) {
            history.value.currentIndex++
          }
        },

        go(delta: number): void {
          const newIndex = history.value.currentIndex + delta
          if (newIndex >= 0 && newIndex < history.value.entries.length && newIndex !== history.value.currentIndex) {
            history.value.currentIndex = newIndex
          }
        },
      },
      configurable: true,
      enumerable: true,
    })

    window.addEventListener('message', auth)

    window.parent.postMessage({ action: 'ready' }, '*')
  }
})

</script>

<template>
  <div class="wrapper">
    <main v-if="isAuth || isIframe">
      <slot />
    </main>
    <div
      v-else
      class="loading-area"
    >
      <div
        class="spinner-border"
        role="status"
      />
    </div>
  </div>
  <RAlert />
  <Teleport to="body">
    <ModalsContainer />
  </Teleport>
</template>

<style lang="scss" scoped>
.wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100dvh;
  width: 100%;
  position: relative;
  overflow-x: hidden;

  main {
    display: flex;
    flex: 1 0 auto;
    flex-direction: column;
    overflow-x: auto;
  }
}

.loading-area {
  position: absolute;
  width: 100%;
  text-align: center;

  .spinner-border {
    color: $bg-accent;
    margin-top: calc(100% / 12);
  }
}
</style>
